// LICENSE_CODE ZON
'use strict'; /*jslint browser:true, es6:true, node:true, react:true*/
import React, {useState, useRef, useEffect, CSSProperties} from 'react';
import cn from 'classnames';
import Icon_arrow_left from '../../../hola/pub/img/hola2023/icons/24/arrow_left.svg';
import Icon_arrow_right from '../../../hola/pub/img/hola2023/icons/24/arrow_right.svg';
import {use_swipe} from '../../../hola/pub/ui/use_swipe';
import styles from './carusel.module.less';

interface i_control_btn_props {
    on_click: ()=>void;
    cls?: string;
    children?: React.ReactNode;
}

interface i_carusel_props {
    items: JSX.Element[];
    slides_gap?: number;
    slide_width?: number;
    slide_height?: number;
    list?: number;
    show_more?: string | JSX.Element;
    control_comp?: React.FC<i_control_btn_props>;
    cls?: {
        root?: string;
        slides?: string;
        controls?: string;
    };
    align_mode?: 'center_viewport' | 'center_container' | 'left';
    controls_icons?: {
        next: JSX.Element;
        prev: JSX.Element;
    };
    highlight_center?: boolean;
}

const Default_control_comp: React.FC<i_control_btn_props> = props=>{
    const {on_click, cls, children} = props;
    return <button className={cls} onClick={on_click}>
      {children}
    </button>;
};

export const Carusel = (props: i_carusel_props)=>{
    const {items, cls = {}, slide_width = 448, slide_height = 376,
        slides_gap = 40, list, show_more = 'Show more', control_comp,
        align_mode = 'center_viewport', controls_icons,
        highlight_center = false} = props;
    const [shown, set_shown] = useState(list);
    const [center_index, set_center_index] = useState(0);
    const s_ref = useRef<HTMLDivElement>();
    const ll_ref = useRef<HTMLDivElement>();
    const l_ref = useRef<HTMLDivElement>();
    const c_ref = useRef<HTMLDivElement>();
    const r_ref = useRef<HTMLDivElement>();
    const rr_ref = useRef<HTMLDivElement>();
    const pos = useRef(0);
    const get_normalized_index = ()=>{
        const len = items.length;
        return (-pos.current % len + len) % len;
    };
    const slides = items.map((content, index)=>{
        const s_style: CSSProperties = {
            marginLeft: `${slides_gap/2}px`,
            marginRight: `${slides_gap/2}px`,
            width: slide_width,
            height: list ? undefined : slide_height,
        };
        const is_center = highlight_center && index === center_index;
        return <div key={index} className={cn(styles.slide,
          highlight_center && styles.highlight_mode,
          is_center && styles.center_slide)} style={s_style}>
          {content}
        </div>;
    });
    const update_slider_pos = ()=>{
        if (list || !s_ref.current)
            return;
        let shift = pos.current * (slide_width + slides_gap);
        if (align_mode == 'left')
            shift -= slides_gap / 4;
        else if (align_mode == 'center_viewport')
            shift += window.innerWidth/2 - slide_width/2 - slides_gap/2;
        else if (align_mode == 'center_container')
        {
            const carousel_rect = s_ref.current.parentElement
                .getBoundingClientRect();
            shift += carousel_rect.width/2 - slide_width/2 - slides_gap/2;
        }
        s_ref.current.style.transform = `translateX(${shift}px)`;
        const panel_width = (slide_width + slides_gap) * items.length;
        const c = Math.floor(pos.current / items.length);
        ll_ref.current.style.left = `${(-2 - c) * panel_width}px`;
        l_ref.current.style.left = `${(-1 - c) * panel_width}px`;
        c_ref.current.style.left = `${-c * panel_width}px`;
        r_ref.current.style.left = `${(1 - c) * panel_width}px`;
        rr_ref.current.style.left = `${(2 - c) * panel_width}px`;
        if (highlight_center)
            set_center_index(get_normalized_index());
    };
    useEffect(()=>{
        update_slider_pos();
        window.addEventListener('resize', update_slider_pos);
        return ()=>{
            window.removeEventListener('resize', update_slider_pos);
        };
    }, [update_slider_pos]);
    const on_left = ()=>{
        if (list || !s_ref.current)
            return;
        pos.current -= 1;
        update_slider_pos();
    };
    const on_right = ()=>{
        if (list || !s_ref.current)
            return;
        pos.current += 1;
        update_slider_pos();
    };
    const {on_touch_move, on_touch_start} = use_swipe({
        on_right,
        on_left,
    });
    const Control = control_comp || Default_control_comp;
    return <div className={cn(styles.carusel, cls.root)}>
      {list ? <div className={styles.list} style={{gap: `${slides_gap}px`}}>
        {slides.slice(0, shown)}
      </div> : <div className={cn(styles.slides, cls.slides)} ref={s_ref}
        style={{height: `${slide_height}px`}}
        onTouchStart={on_touch_start}
        onTouchMove={on_touch_move}
      >
        <div className={styles.panel} ref={ll_ref}>{slides}</div>
        <div className={styles.panel} ref={l_ref}>{slides}</div>
        <div className={styles.panel} ref={c_ref}>{slides}</div>
        <div className={styles.panel} ref={r_ref}>{slides}</div>
        <div className={styles.panel} ref={rr_ref}>{slides}</div>
      </div>}
      {!list ? <div className={cn(styles.controls, cls.controls)}>
        <Control cls={styles.prev} on_click={on_right}>
          {controls_icons?.prev || <Icon_arrow_left/>}
        </Control>
        <Control cls={styles.next} on_click={on_left}>
          {controls_icons?.next || <Icon_arrow_right/>}
        </Control>
      </div> : shown < items.length ? <a style={{marginTop: slides_gap}}
        onClick={()=>{ set_shown(items.length); }}>{show_more}</a> : null}
    </div>;
};
